import { useState, forwardRef, useImperativeHandle, useEffect, useRef } from 'react';
import { TextField, Tabs, Tab, Box, Typography, FormControl, InputLabel } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { langs, createLanguageObject } from '@utils/helpers';
import { Editor } from '@tinymce/tinymce-react';
import TemplateSelect from '@components/workflow/messagedialog/TemplateSelect';
import MergeFieldHelp from '@components/workflow/messagedialog/MergeFieldHelp';
import 'tinymce';
import 'tinymce/skins/ui/oxide/skin.min.css';

const MessageContent = forwardRef(({ initialContent = '' }, ref) => {
    const { t, i18n } = useTranslation();
    const [currentTab, setCurrentTab] = useState(0);
    const templateSelectRef = useRef(null);
    const [templateKey, setTemplateKey] = useState('responsive');
    const [content, setContent] = useState(createLanguageObject());
    const [footer, setFooter] = useState(createLanguageObject());
    const [subject, setSubject] = useState(createLanguageObject());
    const [isRawHtml, setIsRawHtml] = useState(false);

    // Configuration de TinyMCE
    const tinyConfig = {
        license_key: 'gpl',
        menubar: false,
        plugins: [
            'autolink', 'lists', 'link', 'image', 'code', 'fullscreen',
            'help', 'wordcount', 'emoticons'
        ],
        toolbar: 'bold italic underline forecolor | fontsize fontfamily | ' +
            'emoticons image link unlink | bullist numlist | alignleft aligncenter alignright alignjustify | ' +
            'outdent indent | undo redo | code',
        content_style: "body { font-size:16px; font-family: Roboto, Helvetica, Arial, sans-serif,'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol' }",
        base_url: '/tinymce',
        content_css: 'default',
        skin: 'oxide',
        help_tabs: ['shortcuts'],
        browser_spellcheck: true,
        contextmenu: false,
        branding: false,
        images_upload_handler: (blobInfo, progress) => {
            const reader = new FileReader();
            reader.readAsDataURL(blobInfo.blob());
            return new Promise((resolve, reject) => {
                reader.onload = () => {
                    resolve(reader.result);
                };
                reader.onerror = () => {
                    reject(new Error('Erreur lors de la conversion de l\'image en base64'));
                };
            });
        },
        paste_data_images: true,
        paste_as_text: false,
        object_resizing: true
    };
    // Modifier la config de TinyMCE pour rendre la langue dynamique + options
    const getTinyConfig = (langCode, big, rawHtml) => {
        let config = tinyConfig;
        if (rawHtml) {
            config.content_style = "body { font-size:26px; font-family: Roboto, Helvetica, Arial, sans-serif,'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol' }";
        }
        if (langCode === 'fr') {
            return {
                ...config,
                language: 'fr_FR',
                language_url: '/tinymce/langs/fr_FR.js',
                height: big ? 360 : 180
            };
        }
        return config;
    };

    useEffect(() => {
        setIsRawHtml(templateKey === 'html');
    }, [templateKey]);

    useEffect(() => {
        if (!initialContent) {
            return;
        }

        try {
            const parsedContent = JSON.parse(initialContent);
            setTemplateKey(parsedContent.template);
            setContent(createLanguageObject('', lang => parsedContent[lang]?.content || ''));
            setSubject(createLanguageObject('', lang => parsedContent[lang]?.subject || ''));
            setFooter(createLanguageObject('', lang => parsedContent[lang]?.footer || ''));
        } catch (error) {
            console.error('Error parsing initial content:', error);
        }
    }, [initialContent]);

    const handleFieldUpdate = (setter, language, value) => {
        setter(prevState => ({
            ...prevState,
            [language]: value
        }));
    };

    const handleTextFieldChange = (setter, language) => (event) => {
        handleFieldUpdate(setter, language, event.target.value);
    };

    const handleEditorChange = (setter, language) => (content, editor) => {
        handleFieldUpdate(setter, language, content);
    };

    const handleEditorInit = (langCode) => (event, editor) => {
        const editableElement = editor.getBody();
        editableElement.setAttribute('lang', langCode);
    };

    const getFormattedContent = () => {
        let result = Object.keys(langs).reduce((formattedContent, language) => {
            formattedContent[language] = {
                subject: subject[language],
                content: content[language],
                footer: isRawHtml ? '' : footer[language]
            };
            return formattedContent;
        }, {});
        result.template = templateKey;
        return result;
    };

    useImperativeHandle(ref, () => ({
        getContent: getFormattedContent
    }));

    const handleTabChange = (event, newValue) => {
        setCurrentTab(newValue);
    };

    const languageEntries = Object.entries(langs);

    return (
        <Box>
            <FormControl variant="outlined" fullWidth size="small" sx={{ mt: 1 }}>
                <InputLabel id="template-label">{t('Layout')}</InputLabel>
                <TemplateSelect
                    labelId="template-label"
                    emptyLabel={null}
                    variant="outlined"
                    ref={templateSelectRef}
                    value={templateKey}
                    onChange={(event) => setTemplateKey(event.target.value)}
                />
            </FormControl>

            <MergeFieldHelp />
            {isRawHtml && (
                <Typography variant="body2" color="textSecondary" sx={{ mt: 2, mb: 1 }}>
                    {t('Warning: with this layout, no unsubscribe button or link is automatically added to the email.')}
                    <br />
                    {t('You must manually insert the unsubscribe link: click on the "Link" icon and enter the following URL:')}
                    <Box component="code" sx={{ 
                        backgroundColor: 'grey.100', 
                        padding: '2px 4px', 
                        borderRadius: '4px',
                        fontFamily: 'monospace',
                        fontSize: '0.875em',
                        fontWeight: 'bold'
                    }}>
                        {'{url.unsubscribe}'}
                    </Box>
                    .
                    <br />
                    {t('You can also insert links corresponding to the outgoing connections of the current task: click on the "Link" icon and enter the URL:')}
                    <Box component="code" sx={{ 
                        backgroundColor: 'grey.100', 
                        padding: '2px 4px', 
                        borderRadius: '4px',
                        fontFamily: 'monospace',
                        fontSize: '0.875em',
                        fontWeight: 'bold'
                    }}>
                        {'{url.Flow_id}'}
                    </Box>
                    , {t('replacing "Flow_id" with the ID of the outgoing connection.')}
                    <br />
                    {t('If the form submission option is enabled for the current task, you can insert a link to the form: click on the "Link" icon and enter the following URL:')}
                    <Box component="code" sx={{ 
                        backgroundColor: 'grey.100', 
                        padding: '2px 4px', 
                        borderRadius: '4px',
                        fontFamily: 'monospace',
                        fontSize: '0.875em',
                        fontWeight: 'bold'
                    }}>
                        {'{url.form}'}
                    </Box>
                    .
                </Typography>
            )}

            <Tabs
                value={currentTab}
                onChange={handleTabChange}
                sx={{ mt: 1 }}
            >
                {languageEntries.map(([langCode, langLabel], index) => (
                    <Tab key={langCode} label={t('lang.' + langCode)} />
                ))}
            </Tabs>

            {languageEntries.map(([langCode, langLabel], index) => (
                <Box key={langCode} hidden={currentTab !== index}>
                    <TextField
                        fullWidth
                        label={t('Subject') + ' (' + t('lang.' + langCode) + ')'}
                        value={subject[langCode]}
                        onChange={handleTextFieldChange(setSubject, langCode)}
                        variant="outlined"
                        margin="normal"
                    />
                    <Typography variant="body1" sx={{ mt: 2, mb: 1 }}>
                        {t('E-mail content') + ' (' + t('lang.' + langCode) + ')'}
                    </Typography>
                    <Editor
                        value={content[langCode]}
                        init={getTinyConfig(i18n.language, true, isRawHtml)}
                        onEditorChange={handleEditorChange(setContent, langCode)}
                        onInit={handleEditorInit(langCode)}
                    />
                    {!isRawHtml && (<>
                        <Typography variant="body1" sx={{ mt: 2, mb: 1 }}>
                            {t('Footer') + ' (' + t('lang.' + langCode) + ')'}
                        </Typography>
                        <Editor
                            value={footer[langCode]}
                            init={getTinyConfig(i18n.language, false, false)}
                            onEditorChange={handleEditorChange(setFooter, langCode)}
                            onInit={handleEditorInit(langCode)}
                        />
                    </>)}
                </Box>
            ))}
        </Box>
    );
});

export default MessageContent; 